import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, Loader2 } from 'lucide-react';
import { UsernameValidator } from '../utils/usernameValidator';
import { supabase } from '../services/supabase';

interface UsernameInputProps {
  value: string;
  onChange: (value: string) => void;
  userId?: string;
  disabled?: boolean;
}

export const UsernameInput: React.FC<UsernameInputProps> = ({ 
  value, 
  onChange, 
  userId, 
  disabled = false 
}) => {
  const [isChecking, setIsChecking] = useState(false);
  const [validation, setValidation] = useState<{ isValid: boolean; error?: string } | null>(null);
  const [availability, setAvailability] = useState<{ available: boolean; error?: string } | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    if (value && value.length >= 3) {
      validateUsername(value);
    } else {
      setValidation(null);
      setAvailability(null);
      setSuggestions([]);
    }
  }, [value]);

  const validateUsername = async (username: string) => {
    // Client-side validation
    const validationResult = UsernameValidator.validate(username);
    setValidation(validationResult);

    if (validationResult.isValid) {
        // Check availability
        setIsChecking(true);
        try {
          const availabilityResult = await UsernameValidator.checkAvailability(username, supabase, userId);
          setAvailability(availabilityResult);
        
        if (!availabilityResult.available) {
          setSuggestions(UsernameValidator.generateSuggestions(username));
        }
      } catch (error) {
        console.error('Error checking availability:', error);
      } finally {
        setIsChecking(false);
      }
    } else {
      setAvailability(null);
      setSuggestions([]);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    onChange(suggestion);
    setShowSuggestions(false);
  };

  const getStatusIcon = () => {
    if (isChecking) {
      return <Loader2 className="w-4 h-4 animate-spin text-blue-500" />;
    }
    
    if (validation?.isValid && availability?.available) {
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    }
    
    if (validation?.error || availability?.error) {
      return <XCircle className="w-4 h-4 text-red-500" />;
    }
    
    return null;
  };

  const getStatusMessage = () => {
    if (isChecking) return 'Checking availability...';
    if (validation?.error) return validation.error;
    if (availability?.error) return availability.error;
    if (validation?.isValid && availability?.available) return 'Username is available!';
    return null;
  };

  const getStatusColor = () => {
    if (isChecking) return 'text-blue-600';
    if (validation?.isValid && availability?.available) return 'text-green-600';
    if (validation?.error || availability?.error) return 'text-red-600';
    return 'text-gray-600';
  };

  return (
    <div className="space-y-2">
      <div className="relative">
        <div className="flex items-center space-x-2">
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Choose a username"
            disabled={disabled}
            className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              validation?.isValid && availability?.available
                ? 'border-green-500'
                : validation?.error || availability?.error
                ? 'border-red-500'
                : 'border-gray-300'
            } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            onFocus={() => setShowSuggestions(suggestions.length > 0)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          />
          {getStatusIcon()}
        </div>
        
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10">
            <div className="p-2">
              <p className="text-sm text-gray-600 mb-2">Suggestions:</p>
              <div className="space-y-1">
                {suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="w-full text-left px-2 py-1 text-sm hover:bg-gray-100 rounded"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      
      {getStatusMessage() && (
        <div className={`flex items-center space-x-1 text-sm ${getStatusColor()}`}>
          <AlertCircle className="w-4 h-4" />
          <span>{getStatusMessage()}</span>
        </div>
      )}
      
      <div className="text-xs text-gray-500">
        <p>• 3-30 characters</p>
        <p>• Letters, numbers, underscores, and hyphens only</p>
        <p>• Cannot start or end with special characters</p>
        <p>• Used for sharing: @{value || 'username'}</p>
      </div>
    </div>
  );
};
